import { Popover } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import IconButton from 'pf9-ui-components/built/elements/button/IconButton'
import { path, prop } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { ICON_STATUS_COLOR_MAP, ICON_STATUS_MAP } from 'app/constants'
import type { RootState } from 'app/store'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import InfoTooltip from 'core/components/InfoTooltip'
import PollingData from 'core/components/PollingData'
import Progress from 'core/components/progress/Progress'
import SimpleLink from 'core/components/SimpleLink'
import Badge from 'core/elements/badge'
import type { BadgeVariant } from 'core/elements/badge/Badge'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import colors from 'core/themes/base/colors'
import type Theme from 'core/themes/model'
import { getNextUpgradeVersion, isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { extractSupportedK8sVersions } from 'k8s/components/cluster/deployment/form-components/helpers'
import { KaapiClusterStatusBadge } from 'k8s/components/clusters-dashboard/ListK8sClusters'
import EllipsisText from 'k8s/components/common/EllipsisText'
import AvailableUpgrade from 'k8s/components/dashboard/AvailableUpgrade'
import { ClusterUpgradeStatus } from 'k8s/components/kaapi/cluster-upgrade/model'
import { listKaapiConfigMaps } from 'k8s/components/kaapi/config-maps/actions'
import { kaapiConfigMapsSelector } from 'k8s/components/kaapi/config-maps/selectors'
import { isNilOrEmpty } from 'utils/fp'
import { listKaapiClusters } from '../../components/kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from '../../components/kaapi/clusters/selectors'
import { listKaapiClusterUpgradeJobs } from '../kaapi/cluster-upgrade/actions'
import ClusterStatusSpan from '../resources/persistent-volume-claims/ClusterStatusSpan'
import {
  getClusterEndpoint,
  getClusterStatus,
  isKaapiClusterHealthy,
  isTimeExpired,
} from './helpers'

interface Props {
  deleteClusterAction: () => void
}

const UpgradeStatus = ({ value, cluster }) => {
  const isClusterHealthy = isKaapiClusterHealthy(cluster)
  const clusterStatus = useMemo(() => getClusterStatus(cluster), [cluster])
  const classes = useStyles()
  const { history } = useReactRouter()
  const { activeKaapiTenant: namespace } = useSelector<RootState, SessionState>(
    prop(sessionStoreKey),
  )
  useListAction(listKaapiConfigMaps, {
    params: {
      namespace,
    },
    requiredParams: ['namespace'],
  })
  const configMaps = useSelector(kaapiConfigMapsSelector)
  const supportedK8sVersions = useMemo(
    () => extractSupportedK8sVersions(configMaps),
    [configMaps],
  ).sort()

  const clusterUpgradeJob = (cluster?.upgradeJobs || []).find(
    (job) => job?.clusterName === cluster?.metadata?.name,
  )

  const isTimeWithinTwoDays = useMemo(
    () => isTimeExpired(clusterUpgradeJob?.status?.completionTime),
    [clusterUpgradeJob?.status?.completionTime],
  )

  const clusterUpgradeBadgeMap = {
    [ClusterUpgradeStatus.Completed]: { label: 'Completed', variant: 'success' },
    [ClusterUpgradeStatus.InProgress]: { label: 'In Progress', variant: '' },
    [ClusterUpgradeStatus.Failed]: { label: 'Failed', variant: 'error' },
    [ClusterUpgradeStatus.Unknown]: { label: 'Unknown', variant: 'unknown' },
    Latest: { label: 'Latest stable', variant: 'unknown' },
  }

  const phase = clusterUpgradeJob?.phase || ClusterUpgradeStatus.Unknown
  const currentVersion = value

  const nextUpgradeVersion = useMemo(
    () => getNextUpgradeVersion(currentVersion, supportedK8sVersions),
    [currentVersion, supportedK8sVersions],
  )

  const isUpgradeJobAvailable = !isNilOrEmpty(clusterUpgradeJob)

  const isLatestStable = isUpgradeJobAvailable
    ? currentVersion === nextUpgradeVersion && !isTimeWithinTwoDays
    : currentVersion === nextUpgradeVersion
  const isUpgradeInProgress = phase === ClusterUpgradeStatus.InProgress
  const isUpgradeComplete =
    phase === ClusterUpgradeStatus.Completed &&
    currentVersion === nextUpgradeVersion &&
    isTimeWithinTwoDays
  const isAvailableUpgrade = currentVersion !== nextUpgradeVersion && !isUpgradeInProgress

  const isUpgradeFailed = phase === ClusterUpgradeStatus.Failed
  const isCompletedAndWithhinTwoDays =
    phase === ClusterUpgradeStatus.Completed && isTimeWithinTwoDays
  let content

  if (isUpgradeFailed) {
    content = (
      <>
        <div className={classes.statusContainer} data-testid="upgrade-failed-status-container">
          <Text variant="caption1" data-testid="upgrade-failed-version-text">
            {value}
          </Text>
          <Badge
            variant={clusterUpgradeBadgeMap[phase]?.variant}
            text={`Upgrade ${clusterUpgradeBadgeMap[phase]?.label}`}
            data-testid="upgrade-failed-badge"
          />
          <ExternalLink
            url=""
            className={classes.seeDetailsLink}
            onClick={() => {
              history.push(
                routeManager.routes.kubernetes.infrastructure.upgradeStatus.path({
                  cluster: cluster?.metadata?.name,
                }),
              )
            }}
            data-testid="upgrade-failed-see-details-link"
          >
            See Details
          </ExternalLink>
        </div>
        <ExternalLink
          url=""
          className={classes.retryUpgrade}
          onClick={() => {
            history.push(
              routeManager.routes.kubernetes.infrastructure.clusterUpgrade.path({
                cluster: cluster?.metadata?.name,
              }),
            )
          }}
          data-testid="upgrade-failed-retry-upgrade-link"
        >
          <FontAwesomeIcon size="md" data-testid="retry-upgrade-icon">
            refresh
          </FontAwesomeIcon>
          Retry Upgrade
        </ExternalLink>
      </>
    )
  } else if (isUpgradeComplete) {
    content = (
      <div className={classes.statusContainer} data-testid="upgrade-complete-status-container">
        <Text variant="caption1" data-testid="upgrade-complete-version-text">
          {value}
        </Text>
        <Badge
          variant={clusterUpgradeBadgeMap['Completed']?.variant as BadgeVariant}
          text={`Upgrade ${clusterUpgradeBadgeMap['Completed']?.label}`}
          data-testid="upgrade-complete-badge"
        />
        <ExternalLink
          url=""
          className={classes.seeDetailsLink}
          onClick={() => {
            history.push(
              routeManager.routes.kubernetes.infrastructure.upgradeStatus.path({
                cluster: cluster?.metadata?.name,
              }),
            )
          }}
          data-testid="upgrade-complete-see-details-link"
        >
          See Details
        </ExternalLink>
      </div>
    )
  } else if (isAvailableUpgrade && clusterStatus !== ClusterUpgradeStatus.Upgrading) {
    content = (
      <>
        <div className={classes.statusContainer} data-testid="available-upgrade-status-container">
          <Text variant="caption1" data-testid="available-upgrade-version-text">
            {value}
          </Text>
          {isCompletedAndWithhinTwoDays && (
            <Badge
              variant={clusterUpgradeBadgeMap['Completed']?.variant as BadgeVariant}
              text={`Upgrade ${clusterUpgradeBadgeMap['Completed']?.label}`}
              data-testid="available-upgrade-completed-badge"
            />
          )}
          {isCompletedAndWithhinTwoDays && (
            <ExternalLink
              url=""
              className={classes.seeDetailsLink}
              onClick={() => {
                history.push(
                  routeManager.routes.kubernetes.infrastructure.upgradeStatus.path({
                    cluster: cluster?.metadata?.name,
                  }),
                )
              }}
              data-testid="available-upgrade-see-details-link"
            >
              See Details
            </ExternalLink>
          )}
          {!isCompletedAndWithhinTwoDays &&
            (!isClusterHealthy ? (
              <InfoTooltip info="Upgrades can only be performed when cluster status is healthy.">
                <AvailableUpgrade
                  className={classes.disableSelection}
                  cluster={cluster}
                  data-testid="available-upgrade-disable-selection"
                />
              </InfoTooltip>
            ) : !isUpgradeJobAvailable ? (
              <AvailableUpgrade
                className={classes.availableUpgrade}
                cluster={cluster}
                data-testid="available-upgrade-available"
              />
            ) : null)}
        </div>
        {isUpgradeJobAvailable && (
          <AvailableUpgrade
            className={classes.availableUpgrade}
            cluster={cluster}
            data-testid="available-upgrade-available-job"
          />
        )}
      </>
    )
  } else if (isUpgradeInProgress || clusterStatus === ClusterUpgradeStatus.Upgrading) {
    content = (
      <div className={classes.statusContainer} data-testid="upgrade-in-progress-status-container">
        <Text variant="caption1" data-testid="upgrade-in-progress-version-text">
          {value}
        </Text>
        <ClusterStatusSpan
          iconStatus
          status="loading"
          data-testid="upgrade-in-progress-status-span"
        >
          <Text variant="caption1" data-testid="upgrade-in-progress-text">
            Upgrade In Progress
          </Text>
        </ClusterStatusSpan>
        <ExternalLink
          url=""
          className={classes.seeDetailsLink}
          onClick={() => {
            history.push(
              routeManager.routes.kubernetes.infrastructure.upgradeStatus.path({
                cluster: cluster?.metadata?.name,
              }),
            )
          }}
          data-testid="upgrade-in-progress-see-details-link"
        >
          See Details
        </ExternalLink>
      </div>
    )
  } else if (isLatestStable) {
    content = (
      <div className={classes.cardContent} data-testid="latest-stable-card-content">
        <Text variant="caption1" data-testid="latest-stable-version-text">
          {value}
        </Text>
        <Badge
          variant={clusterUpgradeBadgeMap['Latest']?.variant as BadgeVariant}
          text={`${clusterUpgradeBadgeMap['Latest']?.label}`}
          className={classes.latestBadge}
          data-testid="latest-stable-badge"
        />
      </div>
    )
  } else {
    content = (
      <Text variant="caption1" data-testid="default-version-text">
        {value}
      </Text>
    )
  }

  return <>{content}</>
}

const ClusterOverview = ({ deleteClusterAction }: Props) => {
  const classes = useStyles()
  const { history } = useReactRouter()

  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')
  const { loading: loadingKaapiClusters, reload: reloadKaapiClusters } =
    useListAction(listKaapiClusters)
  const clusters = useSelector(kaapiClustersOverviewSelector)

  const [openSettingPopover, setOpenSettingPopover] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const popoverRef = React.useRef(null)

  const handleSettingsClick = useCallback((event) => {
    setOpenSettingPopover(true)
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setOpenSettingPopover(false)
    setAnchorEl(null)
  }, [])

  const handleDeleteCluster = useCallback(() => {
    deleteClusterAction()
    handleClose()
  }, [deleteClusterAction, handleClose])

  // Fetch the list of clusters and filter the one that is currently selected
  const cluster = clusters?.find(
    (cluster) => cluster.metadata.name === k8sPluginGlobalPerfs?.cluster,
  )

  const clusterUpgradeJob = (cluster?.upgradeJobs || []).find(
    (job) => job.clusterName === cluster.metadata.name,
  )

  const overviewBody = [
    {
      label: 'CONTROL PLANE',
      key: 'controlPlaneType',
      defaultValue: 'Managed',
      badge: '',
    },
    {
      label: 'HIGH AVAILABILITY',
      key: 'isHighlyAvailable',
      defaultValue: 'Yes',
      badge: '',
    },
    {
      label: 'API ENDPOINT',
      key: 'spec.controlPlaneEndpoint.host',
      defaultValue: 'cluster.x-k8s.io/v1beta1',
    },
    {
      label: 'INFRASTRUCTURE PROVIDER',
      // key: 'spec.infrastructureRef.kind',
      key: 'infrastructureProvider',
      labelsTooltipDescription: 'INFRASTRUCTURE PROVIDER',
      defaultValue: 'PCD Virtualization',
      badge: '',
    },
    {
      label: 'K8S VERSION',
      key: 'k8Version',
      defaultValue: 'v1.29.5',
      badge: '',
      Component: (value, cluster) => (
        <UpgradeStatus value={value || clusterUpgradeJob?.spec?.sourceVersion} cluster={cluster} />
      ),
    },
    {
      label: 'MANAGEMENT CLUSTER NAMESPACE',
      key: 'metadata.namespace',
      defaultValue: 'default',
      badge: '',
    },
  ]

  return (
    <Progress loading={loadingKaapiClusters}>
      {cluster ? (
        <div className={classes.cardContainer} data-testid="cluster-overview-card-container">
          <div className={classes.cardHeader} data-testid="cluster-overview-card-header">
            <div className={classes.cardContent} data-testid="cluster-overview-card-content">
              <div className={classes.badge} data-testid="cluster-overview-badge">
                <FontAwesomeIcon
                  light
                  size="lg"
                  className="fa-regular"
                  data-testid="cluster-overview-server-icon"
                >
                  server
                </FontAwesomeIcon>
              </div>
              <Text
                variant="caption1"
                className={clsx(classes.headerText, classes.headerSubText)}
                data-testid="cluster-overview-header-text"
              >
                CONFIGURATION OVERVIEW
              </Text>
            </div>
            {/* Cluster Settings */}
            {!isReadonlyUser() && (
              <IconButton
                size="md"
                icon="cog"
                solid={false}
                className={classes.settingsMenuButton}
                onClick={handleSettingsClick}
                nonce={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
                info="Cluster Settings"
                data-testid="cluster-overview-settings-menu-button"
              />
            )}
            {/* Settings Menu */}
            <Popover
              open={openSettingPopover}
              onClose={handleClose}
              anchorEl={anchorEl}
              ref={popoverRef}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              data-testid="cluster-overview-settings-menu-popover"
            >
              <div
                className={classes.settingsMenuContainer}
                data-testid="cluster-overview-settings-menu-container"
              >
                {/* Commented until we add cluster edit functionality */}
                {/* <Text variant="body2" className={clsx(classes.settingsMenuItem)} onClick={() => {}}>
                  <FontAwesomeIcon size="md">edit</FontAwesomeIcon>
                  Edit
                </Text> */}
                <Text
                  variant="body2"
                  className={clsx(classes.settingsMenuItem)}
                  onClick={handleDeleteCluster}
                  data-testid="cluster-overview-delete-cluster-menu-item"
                >
                  <FontAwesomeIcon size="md">trash-can</FontAwesomeIcon>&nbsp;&nbsp;Delete Cluster
                </Text>
              </div>
            </Popover>
          </div>
          <div
            className={clsx(classes.cardContent, classes.content)}
            data-testid="cluster-overview-card-content"
          >
            <EllipsisText
              variant="subtitle1"
              title={cluster?.metadata?.name}
              maxWidth={230}
              showTooltip
              data-testid="cluster-overview-cluster-name-ellipsis-text"
            >
              {cluster?.metadata?.name}
            </EllipsisText>
            <div className={classes.headerSubText} data-testid="cluster-overview-header-sub-text">
              <KaapiClusterStatusBadge
                cluster={cluster}
                data-testid="cluster-overview-cluster-status-badge"
              />
            </div>
          </div>
          <div className={classes.cardsRow} data-testid="cluster-overview-cards-row">
            {overviewBody.map(({ key, label, defaultValue, badge, Component }, index) => {
              const value = path(key.split('.'), cluster) || defaultValue

              return (
                <div key={`key-${index}`} data-testid={`cluster-overview-card-${index}`}>
                  <Text
                    variant="body2"
                    className={clsx(classes.headerText, classes.customText)}
                    data-testid={`cluster-overview-card-${index}-header-text`}
                  >
                    {label}
                  </Text>
                  {Component ? (
                    Component(value, cluster)
                  ) : (
                    <div
                      className={classes.cardContent}
                      data-testid={`cluster-overview-card-${index}-card-content`}
                    >
                      <Text
                        variant="caption1"
                        className={label === 'API ENDPOINT' ? classes.apiEndpointValue : undefined}
                        data-testid={
                          label === 'API ENDPOINT'
                            ? 'cluster-overview-api-endpoint-value'
                            : `cluster-overview-card-${index}-text`
                        }
                      >
                        {label === 'API ENDPOINT' ? getClusterEndpoint(value) : (value as string)}
                      </Text>
                      {badge && (
                        <div
                          className={classes.headerSubText}
                          data-testid={`cluster-overview-card-${index}-badge`}
                        >
                          <Badge
                            data-testid={`cluster-overview-card-${index}-badge`}
                            variant={ICON_STATUS_COLOR_MAP[badge] || 'unknown'}
                            text={ICON_STATUS_MAP[badge]}
                            ellipsisAt={20}
                            tooltipBody={badge?.length > 20 && ICON_STATUS_MAP[badge]}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ) : null}
    </Progress>
  )
}

export default ClusterOverview

const useStyles = makeStyles((theme: Theme) => ({
  cardsRow: {
    display: 'grid',
    marginTop: '8px',
    gridColumnGap: '32px',
    gridRowGap: '24px',
    alignItems: 'center',
    paddingTop: '48px',
  },
  cardContainer: {
    backgroundColor: theme.components.card.background,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '4px 4px 4px 4px',
    padding: '24px',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  headerText: {
    color: theme.components.card.passiveText,
  },
  customText: {
    fontSize: '12px',
    paddingBottom: '8px',
  },
  headerSubText: {
    paddingLeft: '8px',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    backgroundColor: colors.blue[900],
    borderRadius: '4px 4px 4px 4px',
    padding: '3px',
    '& i': {
      color: 'white',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    minWidth: 0,
  },
  content: {
    paddingTop: '16px',
  },
  settingsMenuButton: {
    marginLeft: 'auto',
  },
  settingsMenuContainer: {
    padding: '8px 0',
    width: '154px',
  },
  settingsMenuItem: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  availableUpgrade: {
    textAlign: 'center',
  },
  seeDetailsLink: {
    '& span': {
      fontWeight: 600,
    },
  },
  retryUpgrade: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '8px',
    '& i': {
      marginRight: '4px',
      color: theme.palette.primary.main,
    },
    '& span': {
      fontWeight: 600,
    },
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  latestBadge: {
    marginLeft: '16px',
  },
  disableSelection: {
    cursor: 'not-allowed',
    '& > a': {
      textDecoration: 'none',
      pointerEvents: 'none',
      '& > span': {
        textDecoration: 'none',
        color: theme.palette.grey[300],
      },
    },
  },
  apiEndpointValue: {
    overflowWrap: 'anywhere',
  },
}))
